/**
 * Line chart component for trend analysis
 */

import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions
} from 'chart.js';
import BaseChart from './BaseChart';
import { ChartTheme } from '../../utils/chartConfig';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface LineChartProps {
  data: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      borderColor?: string;
      backgroundColor?: string;
      fill?: boolean;
      tension?: number;
      pointRadius?: number;
      pointHoverRadius?: number;
    }>;
  };
  theme: ChartTheme;
  loading?: boolean;
  error?: string | null;
  height?: number;
  className?: string;
  title?: string;
  showLegend?: boolean;
  showGrid?: boolean;
  fillArea?: boolean;
  smooth?: boolean;
}

const LineChart: React.FC<LineChartProps> = ({
  data,
  theme,
  loading = false,
  error = null,
  height = 300,
  className = '',
  title,
  showLegend = true,
  showGrid = true,
  fillArea = false,
  smooth = true
}) => {
  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: title ? {
        display: true,
        text: title,
        color: theme.text,
        font: {
          family: 'Inter, system-ui, sans-serif',
          size: 16,
          weight: 'bold' as const
        }
      } : undefined,
      legend: {
        display: showLegend,
        labels: {
          color: theme.text,
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: theme.background,
        titleColor: theme.text,
        bodyColor: theme.textSecondary,
        borderColor: theme.border,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        titleFont: {
          family: 'Inter, system-ui, sans-serif',
          size: 13,
          weight: 'bold' as const
        },
        bodyFont: {
          family: 'Inter, system-ui, sans-serif',
          size: 12
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: showGrid,
          color: theme.grid,
          drawOnChartArea: false
        },
        ticks: {
          color: theme.textSecondary,
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 11
          }
        }
      },
      y: {
        grid: {
          display: showGrid,
          color: theme.grid,
          drawOnChartArea: false
        },
        ticks: {
          color: theme.textSecondary,
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 11
          }
        }
      }
    },
    elements: {
      line: {
        tension: smooth ? 0.4 : 0
      },
      point: {
        radius: 4,
        hoverRadius: 6,
        borderWidth: 2
      }
    }
  };

  // Process data to add fill areas if needed
  const processedData = {
    ...data,
    datasets: data.datasets.map(dataset => ({
      ...dataset,
      fill: fillArea,
      backgroundColor: fillArea 
        ? dataset.backgroundColor || `${dataset.borderColor}20`
        : dataset.backgroundColor
    }))
  };

  return (
    <BaseChart
      data={processedData}
      options={options}
      theme={theme}
      loading={loading}
      error={error}
      height={height}
      className={className}
    >
      {({ data, options, height }) => (
        <Line data={data} options={options} />
      )}
    </BaseChart>
  );
};

export default LineChart;
